import { cn } from "@/lib/utils";
import React from "react";
import { ArrowRightToLine, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useReservation } from "@/hooks/useReservations";
import { useChatContext } from "stream-chat-react";
import { CheckinStatus, Reservation } from "@/types/reservation";
import { Link } from "react-router-dom";
import { GeneralBookingInfo } from "@/components/pages/chats/component/booking-info-components/GeneralBookingInfo";
import { calculateNightCount } from "@/lib/booking-utils";
import { GuestsBookingInfo } from "@/components/pages/chats/component/booking-info-components/GuestsBookingInfo";
import { useTranslation } from "react-i18next";
import { HotelStayTimeline } from "@/components/pages/chats/component/booking-info-components/HotelStayTimeline";
import { CheckinBookingInfo } from "@/components/pages/chats/component/booking-info-components/CheckinBookingInfo";
import { NotesBookingInfo } from "@/components/pages/chats/component/booking-info-components/NotesBookingInfo";
import { RequestsBookingInfo } from "@/components/pages/chats/component/booking-info-components/RequestsBookingInfo";
import { DocumentsBookingInfo } from "@/components/pages/chats/component/booking-info-components/DocumentsBookingInfo";
import { PaymentBookingInfo } from "@/components/pages/chats/component/booking-info-components/PaymentBookingInfo";
import { PaymentBalanceBookingInfo } from "@/components/pages/chats/component/booking-info-components/PaymentBalanceBookingInfo";
import { GuestChannelPreferenceBookingInfo } from "@/components/pages/chats/component/booking-info-components/GuestChannelPreferenceBookingInfo";
import { ReservationLogsBookingInfo } from "@/components/pages/chats/component/booking-info-components/ReservationLogsBookingInfo";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useChatProfileSections } from "@/contexts/ChatProfileSectionsContext";

interface BookingInfoComponentProps {
  className?: string;
  onCollapse?: () => void;
}

export const BookingInfoComponent = ({
  className,
  onCollapse,
}: BookingInfoComponentProps) => {
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const { visibility } = useChatProfileSections();
  const { channel } = useChatContext();

  const channelData = channel?.data;
  const bookingId = channelData?.bookingId || channelData?.metadata?.bookingId;

  const { data: channelRelatedBooking } =
    useReservation<Reservation>(bookingId);

  if (!channelRelatedBooking) {
    return null;
  }

  const nightsCount = calculateNightCount(
    channelRelatedBooking.arrivalDate,
    channelRelatedBooking.departureDate,
  );

  return (
    <div
      className={cn(
        "font-geist flex h-full min-h-0 flex-col gap-6 overflow-x-hidden overflow-y-auto bg-[#FBFAFF] py-4 pr-4 pl-2",
        className,
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-row items-center gap-2">
          <Button
            type="button"
            variant="new_ghost"
            size="icon_sm"
            onClick={onCollapse}
            aria-label={t("chats.collapsePanel")}
            className="shrink-0 !bg-transparent text-neutral-600"
          >
            <ArrowRightToLine className="size-5" aria-hidden />
          </Button>
          <h2 className="text-eathy-comfort-1 text-lg font-semibold">
            {t("chats.reservationProfile")}
          </h2>
        </div>
        {visibility.reservations && (
          <>
            <div className="flex flex-row items-center gap-2 text-sm">
              <span className="text-neutral-500">
                {t("chats.reservationId")}:
              </span>
              <span className="text-eathy-comfort-1 font-medium">
                {channelRelatedBooking.bookingNumber ?? bookingId}
              </span>
              <Link
                to={`/reservations/${bookingId}`}
                className="text-neutral-400 transition-colors hover:text-neutral-600"
                aria-label={t("chats.reservation")}
              >
                <ExternalLink className="size-4" />
              </Link>
            </div>
            <h3 className="text-eathy-comfort-1 text-2xl font-normal">
              {nightsCount}{" "}
              {nightsCount > 1 ? t("chats.nights") : t("chats.night")}
            </h3>
          </>
        )}
      </div>

      {visibility.reservations && (
        <HotelStayTimeline
          bookingDate={channelRelatedBooking.createdAt}
          checkInDate={channelRelatedBooking.arrivalDate}
          checkOutDate={channelRelatedBooking.departureDate}
          checkinCompleted={
            channelRelatedBooking.checkinStatus === CheckinStatus.DONE
          }
          checkoutCompleted={
            channelRelatedBooking.checkoutStatus === CheckinStatus.DONE
          }
        />
      )}

      <GeneralBookingInfo
        booking={channelRelatedBooking}
        showRoomInfo={visibility.rooms}
        className={"flex flex-col gap-1.5"}
      />
      <div className={"flex flex-col gap-2"}>
        <GuestsBookingInfo booking={channelRelatedBooking} />
        <CheckinBookingInfo booking={channelRelatedBooking} />
        <DocumentsBookingInfo booking={channelRelatedBooking} />
        <PaymentBalanceBookingInfo booking={channelRelatedBooking} />
        <PaymentBookingInfo booking={channelRelatedBooking} />
        <GuestChannelPreferenceBookingInfo booking={channelRelatedBooking} />
        {capabilities.canUseRequestsSystem ? (
          <RequestsBookingInfo booking={channelRelatedBooking} />
        ) : null}
        <ReservationLogsBookingInfo bookingId={bookingId} />
        <NotesBookingInfo booking={channelRelatedBooking} />
      </div>
    </div>
  );
};
